/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/15/22
 */

import React from "react";
import {
	Avatar,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	colors,
	Stack,
	Typography,
	useTheme
} from "@mui/material";
import { Employee, Holiday, VegaEvent } from "../interfaces/Global";
import ViewportEvent from "./ViewportEvent";
import countryNames from "../const/country-names";
import stateNames from "../const/state-names";
import { format } from "../dates-helper";
import ModelView, { AttributeConfig } from "../components/ModelView";
import CreateIcon from "@mui/icons-material/Add";
import { addMinutes } from "date-fns";

export interface HolidayViewportEventProps {
	viewportRange: Interval;
	rangeResolution: number;
	intervalDuration: number;
	employee: Employee;
	event: Holiday;
	openNewEventDialog: (day: Partial<VegaEvent>) => void;
}

export default function HolidayViewportEvent(props: HolidayViewportEventProps) {

	const theme = useTheme();

	return (
		<ViewportEvent
			viewportRange={props.viewportRange}
			rangeResolution={props.rangeResolution}
			normalizeTimezone={false}
			details={
				<EventDetails
					event={props.event}
					openNewEventDialog={props.openNewEventDialog}
					intervalDuration={props.intervalDuration}
				/>
			}
			event={{
				title: props.event.title,
				typeId: "OOO",
				startDatetime: new Date(props.event.startDate),
				endDatetime: new Date(props.event.endDate)
			}}
			tooltip={
				<EventTooltip
					event={props.event}
					employee={props.employee}
				/>
			}
			viewportIcon={""}
			getViewportLabel={(width) => {
				return width > 80 ? props.event.title : props.event.title.replace(/[^\w\s-]/gi, "").split(" ").map(i => i[0]).join("");
			}}
			sx={{
				zIndex: 20,
				backgroundColor: theme.palette.mode === "light" ? colors.red[100] : "#8B1A1A"
			}}
		/>
	);

}

interface EventTooltipProps {
	event: Holiday;
	employee: Employee;
}

function EventTooltip({ event, employee }: EventTooltipProps) {

	return (
		<Stack>
			<Stack>{employee.displayName}</Stack>
			<Typography>{event.title}</Typography>
			<Stack>Holiday</Stack>
			<Stack>{countryNames[event.countryCode]}{event.state ? " (" + event.state + ")" : ""}</Stack>
			<Stack>{format(event.startDate, "PPPP")}</Stack>
		</Stack>
	);

}

interface EventDetailsProps {
	event: Holiday;
	openNewEventDialog: (day: Partial<VegaEvent>) => void;
	intervalDuration: number;
}

function EventDetails({ event, openNewEventDialog, intervalDuration }: EventDetailsProps) {

	const attributes: AttributeConfig<Holiday>[] = [
		{ attribute: "creationDate", format: "datetime" },
		{ attribute: "countryCode", label: "Country", value: countryNames[event.countryCode] },
		{
			attribute: "state",
			hidden: !event.state,
			value: event.state ? stateNames[event.countryCode]?.[event.state] ?? event.state : ""
		},
		{ attribute: "startDate", format: "date" },
		{ attribute: "endDate", format: "date" },
		{ attribute: "properties", value: event.properties?.source, label: "Source", hidden: !event.properties?.source },
		{ attribute: "notes", hidden: !event.notes }
	];

	return (
		<Card>
			<CardHeader
				avatar={<Avatar src={"https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/4.1.5/flags/4x3/"
					+ (event.countryCode === "UK" ? "GB" : event.countryCode ?? "").toLowerCase() + ".svg"}/>}
				title={event.title}
				subheader={(event.state ? "State" : "National") + " Holiday"}
			/>
			<CardContent>
				<ModelView
					attributes={attributes}
					model={event}
					labelSx={{
						pr: 1,
						width: 130,
						textAlign: "right"
					}}
					valueSx={{
						maxWidth: 300,
						maxHeight: 200,
						overflow: "auto"
					}}
					nullTypographyProps={{
						variant: "body2"
					}}
					labelTypographyProps={{
						color: "textSecondary",
						variant: "body2"
					}}
					valueTypographyProps={{
						variant: "body2"
					}}
				/>
			</CardContent>
			<CardActions disableSpacing>
				<Button
					size="small"
					onClick={() => {
						openNewEventDialog({
							startDatetime: event.startDate,
							endDatetime: addMinutes(event.startDate, intervalDuration),
							typeId: "WFO"
						});
					}}
					startIcon={<CreateIcon/>}
				>
					Override event
				</Button>
			</CardActions>
		</Card>
	);

}